import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import type { OperatorIdentity } from "@proof/operator";
import type { OperatorContext } from "@proof/core";
import { Button, Control } from "./components";
import { instanceRequest, instanceMessage } from "./instance-api";
import { operatorNavigate } from "./OperatorAccess";

export function OperatorTenantEntry({
  identity,
  onChanged,
}: {
  identity: OperatorIdentity;
  onChanged: () => void;
}) {
  const [tenantId, setTenant] = useState(""),
    [reason, setReason] = useState(""),
    [minutes, setMinutes] = useState(5),
    [password, setPassword] = useState(""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [owner, setOwner] = useState(false),
    [target, setTarget] = useState(identity.operatorId),
    [enabled, setEnabled] = useState(true),
    [grantPassword, setGrantPassword] = useState("");
  useEffect(() => {
    const abort = new AbortController();
    void instanceRequest<{ canEditConfiguration: boolean }>(
      "/api/operator/status",
      undefined,
      undefined,
      abort.signal,
    )
      .then((view) => {
        if (!abort.signal.aborted) setOwner(view.canEditConfiguration);
      })
      .catch((cause) => {
        if (!abort.signal.aborted) setError(instanceMessage(cause));
      });
    return () => abort.abort();
  }, []);
  return (
    <main className="instance-shell instance-access">
      <header className="instance-brand">
        Proof Studio <span>Platform operator</span>
      </header>
      <section className="instance-panel">
        <h1>Tenant support access</h1>
        <p>
          Signed in as {identity.username}. Support access uses a temporary
          tenant administrator identity and records your actions.
        </p>
        {identity.canImpersonateTenants ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (busy) return;
              setBusy(true);
              setError("");
              const currentPassword = password;
              setPassword("");
              void instanceRequest("/api/operator/impersonation/start", {
                tenantId,
                reason,
                durationMs: minutes * 60000,
                currentPassword,
              })
                .then(() => {
                  operatorNavigate("/");
                  onChanged();
                })
                .catch((cause) => setError(instanceMessage(cause)))
                .finally(() => setBusy(false));
            }}
          >
            <fieldset disabled={busy} className="instance-form-fields">
              <Control label="Support tenant ID">
                <input
                  value={tenantId}
                  maxLength={200}
                  required
                  onChange={(e) => setTenant(e.target.value)}
                />
              </Control>
              <Control label="Reason for support access">
                <textarea
                  value={reason}
                  minLength={10}
                  maxLength={500}
                  required
                  onChange={(e) => setReason(e.target.value)}
                />
              </Control>
              <Control label="Support duration">
                <select
                  value={minutes}
                  onChange={(e) => setMinutes(Number(e.target.value))}
                >
                  {[5, 10, 15].map((v) => (
                    <option key={v} value={v}>
                      {v} minutes
                    </option>
                  ))}
                </select>
              </Control>
              <Control label="Current operator password">
                <input
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  maxLength={128}
                  required
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Control>
              <p>
                Keep credentials and private customer values out of the reason.
                Stop support access before choosing another tenant.
              </p>
              <Button type="submit" kind="primary">
                {busy
                  ? "Checking support access…"
                  : "Start tenant support access"}
              </Button>
            </fieldset>
          </form>
        ) : (
          <p>
            Your account does not have the tenant impersonation grant. The
            instance owner can change it with fresh password confirmation.
          </p>
        )}
        {owner && (
          <details>
            <summary>Manage impersonation grants</summary>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                if (busy) return;
                setBusy(true);
                setError("");
                const currentPassword = grantPassword;
                setGrantPassword("");
                void instanceRequest("/api/operator/impersonation/grant", {
                  operatorId: target,
                  enabled,
                  currentPassword,
                })
                  .then(() => {
                    onChanged();
                  })
                  .catch((cause) => setError(instanceMessage(cause)))
                  .finally(() => setBusy(false));
              }}
            >
              <fieldset disabled={busy} className="instance-form-fields">
                <Control label="Target operator ID">
                  <input
                    value={target}
                    required
                    onChange={(e) => setTarget(e.target.value)}
                  />
                </Control>
                <Control label="Impersonation grant">
                  <select
                    value={String(enabled)}
                    onChange={(e) => setEnabled(e.target.value === "true")}
                  >
                    <option value="true">Allowed</option>
                    <option value="false">Denied</option>
                  </select>
                </Control>
                <Control label="Owner password for grant change">
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={grantPassword}
                    required
                    maxLength={128}
                    onChange={(e) => setGrantPassword(e.target.value)}
                  />
                </Control>
                <p>
                  Changing a grant ends the target operator's sessions. Changing
                  your own grant requires signing in again.
                </p>
                <Button type="submit">Save impersonation grant</Button>
              </fieldset>
            </form>
          </details>
        )}
        {error && <p role="alert">{error}</p>}
        <Button disabled={busy} onClick={() => operatorNavigate("/operator")}>
          Back to platform status
        </Button>
      </section>
    </main>
  );
}

export function OperatorContextShell({
  context,
  onChanged,
  children,
}: {
  context: OperatorContext;
  onChanged: () => void;
  children: (stop: () => void) => ReactNode;
}) {
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [modalHost, setModalHost] = useState<HTMLElement>();
  const body = useRef<HTMLDivElement>(null),
    releaseModal = useRef(() => {});
  useEffect(() => {
    let active: HTMLDialogElement | undefined, host: HTMLElement | undefined;
    const clear = () => {
      active?.removeEventListener("close", closed);
      host?.remove();
      active = undefined;
      host = undefined;
      setModalHost(undefined);
    };
    const closed = () => {
      if (active && (!active.open || !active.isConnected)) clear();
    };
    const bind = (dialog?: HTMLDialogElement) => {
      if (
        !dialog ||
        dialog === active ||
        !dialog.open ||
        !dialog.matches(":modal")
      )
        return;
      let scope: Node = dialog;
      while (scope.getRootNode() instanceof ShadowRoot)
        scope = (scope.getRootNode() as ShadowRoot).host;
      if (!body.current?.contains(scope)) return;
      clear();
      active = dialog;
      host = document.createElement("div");
      Object.assign(host.style, { position: "sticky", top: "0", zIndex: "20" });
      dialog.prepend(host);
      dialog.addEventListener("close", closed);
      setModalHost(host);
    };
    let live = true;
    const scan = () => {
      if (!live || !body.current) return;
      closed();
      const roots: Node[] = [],
        dialogs: HTMLDialogElement[] = [];
      const visit = (root: HTMLElement | ShadowRoot) => {
        roots.push(root);
        for (const element of root.querySelectorAll("*")) {
          if (
            element instanceof HTMLDialogElement &&
            element.open &&
            element.matches(":modal")
          )
            dialogs.push(element);
          if (element.shadowRoot) visit(element.shadowRoot);
        }
      };
      visit(body.current);
      // Reconnect only live roots so removed widgets and their values can be released.
      observer.disconnect();
      for (const root of roots)
        observer.observe(root, {
          subtree: true,
          childList: true,
          attributes: true,
          attributeFilter: ["open"],
        });
      if (!active) bind(dialogs.at(-1));
    };
    const observer = new MutationObserver((records) => {
      if (!live) return;
      for (const record of records)
        if (
          record.type === "attributes" &&
          record.target instanceof HTMLDialogElement
        )
          bind(record.target);
      scan();
    });
    const focus = (event: FocusEvent) => {
      closed();
      bind(
        event
          .composedPath()
          .find(
            (node): node is HTMLDialogElement =>
              node instanceof HTMLDialogElement &&
              node.open &&
              node.matches(":modal"),
          ),
      );
    };
    releaseModal.current = clear;
    document.addEventListener("focusin", focus, true);
    scan();
    const timer = setInterval(scan, 1000);
    return () => {
      live = false;
      observer.disconnect();
      document.removeEventListener("focusin", focus, true);
      clearInterval(timer);
      active?.removeEventListener("close", closed);
      host?.remove();
      releaseModal.current = () => {};
    };
  }, []);
  useEffect(() => {
    const check = () => {
      if (Date.now() >= context.expiresAt) {
        setBusy(true);
        onChanged();
      }
    };
    check();
    const timer = setInterval(check, 1000);
    return () => clearInterval(timer);
  }, [context.id, context.expiresAt, onChanged]);
  const end = useCallback(
    (logout: boolean) => {
      if (busy) return;
      releaseModal.current();
      setBusy(true);
      setError("");
      void instanceRequest(
        logout ? "/api/operator/logout" : "/api/operator/impersonation/stop",
        {},
      )
        .then(() => {
          operatorNavigate(logout ? "/operator/login" : "/operator");
          onChanged();
        })
        .catch((cause) => {
          setError(instanceMessage(cause));
          setBusy(false);
          onChanged();
        });
    },
    [busy, onChanged],
  );
  const banner = (embedded = false) => (
    <aside
      className="operator-context-banner"
      aria-label="Active tenant support access"
      aria-hidden={!embedded && !!modalHost ? true : undefined}
      style={
        embedded
          ? {
              position: "sticky",
              top: 0,
              zIndex: 20,
              background: "#36213e",
              color: "#fff",
              borderBottom: "4px solid #efbe66",
              padding: "10px",
              marginBottom: "12px",
              font: "12px/1.4 system-ui",
              overflowWrap: "anywhere",
            }
          : modalHost
            ? { visibility: "hidden" }
            : undefined
      }
    >
      <div>
        <strong>Tenant support access · {context.tenantId}</strong>
        <p style={embedded ? { color: "#f6eef9", margin: "4px 0" } : undefined}>
          Operator {context.actorUsername} ·{" "}
          <code
            style={
              embedded
                ? {
                    background: "#51375d",
                    color: "#ffe1a6",
                    overflowWrap: "anywhere",
                  }
                : undefined
            }
          >
            {context.actorOperatorId}
          </code>
        </p>
        <p style={embedded ? { color: "#f6eef9", margin: "4px 0" } : undefined}>
          Effective administrator ·{" "}
          <code
            style={
              embedded
                ? {
                    background: "#51375d",
                    color: "#ffe1a6",
                    overflowWrap: "anywhere",
                  }
                : undefined
            }
          >
            {context.effectiveSubjectId}
          </code>{" "}
          · Ends{" "}
          <time dateTime={new Date(context.expiresAt).toISOString()}>
            {new Date(context.expiresAt).toLocaleTimeString()}
          </time>
        </p>
      </div>
      <div
        className="instance-actions"
        style={
          embedded
            ? { display: "flex", flexWrap: "wrap", gap: "8px" }
            : undefined
        }
      >
        <Button disabled={busy} onClick={() => end(false)}>
          Stop support access
        </Button>
        <Button disabled={busy} onClick={() => end(true)}>
          Log out
        </Button>
      </div>
      {error && <p role="alert">{error}</p>}
    </aside>
  );
  return (
    <div className="operator-context-shell">
      {banner()}
      {modalHost && createPortal(banner(true), modalHost)}
      <div className="operator-context-body" ref={body}>
        {busy ? (
          <p role="status">Checking access…</p>
        ) : (
          children(() => end(false))
        )}
      </div>
    </div>
  );
}
